import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { Urls } from '../lib/urls';
import * as vo from '../core/model/server/vo/_index';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
      private httpService: RequestsService,
  ) {}

  // TRANSACTIONS
  getAllTransactions(request: vo.TransactionsRequest, page: number, size: number): Observable<vo.TransactionsResponse> {
    const url = `${Urls.transactionsAll}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.TransactionsResponse>;
  }

  getBuyloadTransactions(request: vo.BuyloadTransactionsRequest, page: number, size: number): Observable<vo.BuyloadTransactionsResponse> {
    const url = `${Urls.transactionsBuyload}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BuyloadTransactionsResponse>
    );
  }

  getCashInTransactions(request: vo.CashInTransactionsRequest, page: number, size: number): Observable<vo.CashInTransactionsResponse> {
    const url = `${Urls.transactionsCashin}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.CashInTransactionsResponse>
    );
  }

  getPayBillsTransactions(request: vo.PayBillsTransactionsRequest, page: number, size: number): Observable<vo.PayBillsTransactionsResponse> {
    const url = `${Urls.transactionsPaybills}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.PayBillsTransactionsResponse>;
  }

  getTransferFundsTransactions(request: vo.TransferFundsTransactionsRequest, page: number, size: number): Observable<vo.TransferFundsTransactionsResponse> {
    const url = `${Urls.transactionsTransfers}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.TransferFundsTransactionsResponse>;
  }

  getStorePurchaseTransactions(request: vo.StorePurchaseTransactionsRequest, page: number, size: number): Observable<vo.StorePurchaseTransactionsResponse> {
    const url = `${Urls.transactionsPurchases}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.StorePurchaseTransactionsResponse>;
  }

  getMegaRewardTransactions(request: vo.MegaRewardTransactionsRequest, page: number, size: number): Observable<vo.MegaRewardTransactionsResponse> {
    const url = `${Urls.transactionsRewards}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.MegaRewardTransactionsResponse>;
  }

  getOverviewTransactions(request: vo.OverviewTransactionsRequest, page: number, size: number): Observable<vo.OverviewTransactionsResponse> {
    const url = `${Urls.transactionsOverview}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.OverviewTransactionsResponse>;
  }

  exportTransferFundsTransactions(request: vo.ExportTransferFundsTransactionsRequest): Observable<Blob> {
    const url = `${Urls.exportTransactionsTransfers}`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }


  // ACCOUNTS
  getFilteredAccounts(request: vo.FilteredAccountsRequest, page: number, size: number): Observable<vo.FilteredAccountResponse> {
    const url = `${Urls.userAccount}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.FilteredAccountResponse>;
  }

  getAccountById(id: number): Observable<vo.UserAccountResponse> {
    const url = `${Urls.userAccount}/${id}`;
    return from(this.httpService.get(url)) as Observable<vo.UserAccountResponse>;
  }

  updateAccountDetails(id: number, request: vo.UpdateAccountDetailsRequest): Observable<vo.UpdateAccountStatusResponse> {
    const url = `${Urls.userAccount}/${id}`;
    return from(this.httpService.put(url, request)) as Observable<vo.UpdateAccountStatusResponse>;
  }

  updateAccountsEmailStatus(id: number): Observable<vo.BaseResponse> {
    const url = `${Urls.userAccount}/${id}/directVerifyEmail`;
    return from(this.httpService.get(url)) as Observable<vo.BaseResponse>;
  }

  exportAccounts(request: vo.FilteredAccountsRequest): Observable<Blob> {
    const url = `${Urls.exportAccounts}`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  // KYC
  getKycLogs(request: vo.KycLogsRequest, page: number, size: number): Observable<vo.KycLogsResponse> {
    const url = `${Urls.kycLogs}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.KycLogsResponse>;
  }

  getKycRegistrations( request: vo.KycRegistrationRequest, page: number, size: number): Observable<vo.KycRegistrationResponse> {
    const url = `${Urls.kycRegistrations}?page=${page}&size=${size}`;
    return from(this.httpService.post(url, request)) as Observable<vo.KycRegistrationResponse>;
  }

  // ANNOUNCEMENTS
  getAnnouncements(request: vo.FilteredRequest, page: number, size: number): Observable<vo.AnnouncementsResponse> {
    const url = `${Urls.announcements}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.AnnouncementsResponse>
    );
  }

  storeAnnouncement(formData: FormData): Promise<vo.AnnouncementsResponse> {
    return this.httpService.postFormWithImage(
      `${Urls.store_announcement}`,
      formData
    );
  }

  updateAnnouncement(formData: FormData, uid: number): Promise<vo.BaseResponse> {
    return this.httpService.put2(
      `${Urls.announcements}/${uid}`, 
      formData
    );
  }

  setStatusAnnouncement(uid: number, status: number): Observable<vo.BaseResponse> {
    const formData = new FormData();
    formData.append('status', status.toString());
    
    return from(
      this.httpService.put2(`${Urls.announcements}/${uid}`, formData)
    ) as Observable<vo.BaseResponse>;
  }

  exportAnnouncements(request: vo.ExportAnnouncementsRequest): Observable<Blob> {
    const url = `${Urls.exportAnnouncements}`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  // PUSH NOTIFICATIONS

  getPushNotifications(request: vo.FilteredRequest, page: number, size: number): Observable<vo.PushNotificationsResponse> {
    const url = `${Urls.pushNotif}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.PushNotificationsResponse>
    );
  }

  getNotificationById(uid: number): Observable<vo.PushNotificationsResponse> {
    const url = `${Urls.pushNotif}/${uid}/getOne`;
    return from(
      this.httpService.get(url) as Observable<vo.PushNotificationsResponse>
    );
  }

  storePushNotifications(formData: FormData): Observable<vo.PushNotificationsResponse> {
    return from(
      this.httpService.postFormWithImage(`${Urls.pushNotif}/new`, formData)
    ) as Observable<vo.PushNotificationsResponse>;
  }

  setStatusPushNotification(uid: number, status: number): Observable<vo.BaseResponse> {
    const formData = new FormData();
    formData.append('status', status.toString());
    
    return from(
      this.httpService.put2(`${Urls.pushNotif}/${uid}`, formData)
    ) as Observable<vo.BaseResponse>;
  }

  updatePushNotification(formData: FormData, uid: number): Observable<vo.BaseResponse> {
    return from(
      this.httpService.put2(
        `${Urls.pushNotif}/${uid}`, 
        formData
      )
    ) as Observable<vo.BaseResponse>;
  }



  // ADS
  getAds( request: vo.FilteredRequest, page: number, size: number): Observable<vo.AdsResponse> {
    const url = `${Urls.ads}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.AdsResponse>
    );
  }

  storeAds(formData: FormData): Promise<vo.AnnouncementsResponse> {
    return this.httpService.postFormWithImage(
      `${Urls.ads}/new`,
      formData
    );
  }

  setStatusAds(uid: number, status: number): Observable<vo.BaseResponse> {
    const formData = new FormData();
    formData.append('status', status.toString());
    
    return from(
      this.httpService.put2(`${Urls.ads}/${uid}`, formData)
    ) as Observable<vo.BaseResponse>;
  }

  updateAds(formData: FormData, uid: number): Promise<vo.BaseResponse> {
    return this.httpService.put2(
      `${Urls.ads}/${uid}`, 
      formData
    );
  }

  getAdsById(uid: number): Observable<vo.BaseResponse> {
    const url = `${Urls.ads}/${uid}`;
    return from(
      this.httpService.put(url, '') as Observable<vo.BaseResponse>
    );
  }

  exportAds(request: vo.ExportAdsRequest): Observable<Blob> {
    const url = `${Urls.exportAds}`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  // BUYLOAD ITEMS
  getBuyLoadItems( page: number,  size: number,  request: vo.GetBuyLoadItemRequest): Observable<vo.GetBuyLoadItemResponse> {
    const url = `${Urls.buyload}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetBuyLoadItemResponse>
    );
  }

  getBuyLoadItemById( uid: number, request: any): Observable<vo.GetByIdBuyLoadItemResponse> {
    const url = `${Urls.buyload}/getOne`;
    return from(
      this.httpService.post(url, {uid: uid}) as Observable<vo.GetByIdBuyLoadItemResponse>
    );
  }

  storeBuyLoadItem(
    request: vo.CreateBuyLoadItemRequest, 
    isPins: boolean = false
  ): Observable<vo.CreateBuyLoadItemResponse> {
    
    // default: add buyload item ( non gaming pins etc.)
    let urlItem = Urls.store_buyload;

    if(isPins){
      urlItem = Urls.gamingPinsBuyload
    }
    const promiseResult = this.httpService.post(urlItem, request);
    return from(promiseResult) as Observable<vo.CreateBuyLoadItemResponse>;
  }

  updateBuyLoadItem( uid: number, request: vo.UpdateBuyLoadItemRequest): Observable<vo.UpdateBuyLoadItemResponse> {
    const url = `${Urls.buyload}/${uid}`;
    return from(
      this.httpService.put(url, request)as Observable<vo.UpdateBuyLoadItemResponse>
    )
  }

  updateBuyloadItemPins(request: vo.UpdateBuyloadPinsRequest, ){
    const url = Urls.gamingPinsBuyload
    return from(
      this.httpService.post(url, request)as Observable<vo.UpdateBuyloadPinsResponse>
    )
  }

  exportBuyloadItems(request: vo.ExportBuyloadItemsRequest): Observable<Blob> {
    const url = `${Urls.exportBuyload}`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  // CATEGORIES
  getBuyloadCategories( page: number,  size: number,  request: vo.GetBuyloadCategoryRequest): Observable<vo.GetBuyloadCategoryResponse> {
    const url = `${Urls.category}/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetBuyloadCategoryResponse>
    );
  }

  getActiveCategories(page: number,  size: number,  request: vo.GetByStatusBuyloadCategoryRequest): Observable<vo.GetBuyloadCategoryResponse> {
    const url = `${Urls.category}/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetBuyloadCategoryResponse>
    );
  }

  storeCategory(formData: FormData): Promise<vo.CreateBuyLoadCategoryResponse> {
    return this.httpService.postFormWithImage(
      `${Urls.category}/save`,
      formData
    );
  }

  bulkStoreCategories(formData: FormData): Promise<vo.BulkBuyloadCategoryResponse> {
    return this.httpService.postFormWithImage(
      `${Urls.category}/bulkInsert`,
      formData
    );
  }

  updateCategory(formData: FormData): Observable<vo.CreateBuyLoadCategoryResponse> {
    return this.httpService.put2(
      `${Urls.category}/update`, 
      formData
    );
  }

  setStatusCategory(request: vo.StatusBuyloadCategoryRequest): Observable<vo.BaseResponse> {
    return from(
      this.httpService.put(`${Urls.category}/doAction`, request) as  Observable<vo.BaseResponse>
    )
  }

  // PROVIDER
  getLoadProviderList(): Observable<vo.GetProviderResponse> {
    return from(
      this.httpService.get(Urls.providers) as Observable<vo.GetProviderResponse>
    );
  }

  switchLoadProvider(providerDesc: number, request: vo.ProviderSwitchRequest): Observable<vo.ProviderSwitchResponse> {
    const url = `${Urls.providers}/?provider=${providerDesc}`;
    return from(
      this.httpService.put(url, request)as Observable<vo.ProviderSwitchResponse>
    )
  }

  // BILLERS
  getPaginatedBillers(request: vo.BillerRequest,page: number, size: number): Observable<vo.BillerResponse> {
    const url = `${Urls.billers}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BillerResponse>
    );
  }
  
  getBillerFieldNames(): Observable<vo.BillerResponse> {
    const url = `${Urls.fields_billers}`;
    return from(
      this.httpService.get(url) as Observable<vo.BillerResponse>
    );
  }

  saveBiller(formData: FormData): Promise<vo.CreateBillerRequest> {
    return this.httpService.postFormWithImage(
      `${Urls.store_billers}`,
      formData
    );
  }

  updateBiller(uid: number, formData: FormData): Promise<vo.BaseResponse> {
    return this.httpService.put2(
      `${Urls.billers}/${uid}`,
      formData
    );
  }

  updateBillerStatus(uid: number, status: number): Observable<vo.BaseResponse> {
    const formData = new FormData();
    formData.append('status', status.toString());
    
    return from(
      this.httpService.put2(`${Urls.billers}/${uid}`, formData)
    ) as Observable<vo.BaseResponse>;
  }

  // REDEEM PERCENTAGE
  getRedeemPercentage(): Observable<vo.RedeemPercentageResponse> {
    const url = `${Urls.redeem}/getPercentages`;
    return from(
      this.httpService.get(url) as Observable<vo.BillerResponse>
    );
  }

  saveRedeemPercentage(request: vo.UpdateRedeemPercentageRequest): Observable<vo.BaseResponse>{
    const url = `${Urls.redeem}/savePercentages`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  saveRedeemPreSet(request: vo.PresetRedeemPercentageRequest): Observable<vo.BaseResponse>{
    const url = `${Urls.redeem}/savePercentages`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }


  // PROFANITY FILTER
  getProfanityFilterList(): Observable<vo.ProfanityFilterResponse> {
    const url = `${Urls.profanity}/all`;
    return from(
      this.httpService.get(url) as Observable<vo.ProfanityFilterResponse>
    );
  }

  saveProfanityFilterWords(request: vo.ProfanityFilterRequest): Observable<vo.BaseResponse>{
    const url = `${Urls.profanity}/add`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  removeProfanityFilterWords(request: vo.ProfanityFilterRequest): Observable<vo.BaseResponse>{
    const url = `${Urls.profanity}/remove`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }


  // PORTAL USER LOGS
  getPortalUserLogs(page: number,  size: number,  request: vo.PortalUserLogsRequest): Observable<vo.PortalUserLogsResponse> {
    const url = `${Urls.portalUserLogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.PortalUserLogsResponse>
    );
  }

  // PORTAL USERS
  getUsersAll(
    page: number,
    size: number,
    request: vo.GetUserRequest
  ): Observable<vo.GetUserResponse> {
    const url = `${Urls.users}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetUserResponse>
    );
  }

  findOneUser(
    request: vo.FindOneUserRequest
  ): Observable<vo.FindOneUserResponse> {
    return from(
      this.httpService.post(Urls.users, request) as Observable<vo.FindOneUserResponse>
    );
  }

  createUser(
    request: vo.CreateUserRequest
  ): Observable<vo.CreateUserResponse> {
    return from(
      this.httpService.post(`${Urls.users}/new`, request) as Observable<vo.CreateUserResponse>
    );
  }

  updateUser(
    uid: number,
    request: vo.UpdateUserRequest
  ): Observable<vo.UpdateUserResponse> {
    const url = `${Urls.users}/${uid}`;
    return from(
      this.httpService.put(url, request) as Promise<vo.UpdateUserResponse>
    );
  }

  getUserDetails(): Observable<vo.FindOneUserResponse> {
    return from(
      this.httpService.post(`${Urls.users}/show`, {}) as Observable<vo.FindOneUserResponse>
    );
  }

  // REDEEM TRANSACTIONS
  getRedeemTransactions(page: number,  size: number,  request: vo.RedeemTransactionsRequest): Observable<vo.RedeemTransactionsResponse> {
    const url = `${Urls.redeem}/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.RedeemTransactionsResponse>
    );
  }

  // AUTO DISABLE LOGS
  getDisableLogs(page: number, size: number, request: vo.DisableLogsRequest): Observable<vo.DisableLogsResponse> {
    const url = `${Urls.logs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.DisableLogsResponse>
    );
  }


  // REFUND TRANSACTIONS
  refundTransaction(transactionId: string): Observable<vo.BaseResponse> {
    const url = `/buyload/${transactionId}/refund`;
    return from(
      this.httpService.put(url, {}) as Observable<vo.BaseResponse>
    );
  }

  // CASHOUT SETTINGS
  getCashoutSettings(): Observable<vo.GetCashoutSettingsResponse> {
    const url = `${Urls.disbursement}/getSettings`;
    return from(
      this.httpService.get(url) as Observable<vo.GetCashoutSettingsResponse>
    );
  }

  updateCashoutSettings(request: vo.UpdateCashoutSettingsRequest): Observable<vo.BaseResponse> {  
    const url = `${Urls.disbursement}/configureSettings`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  // CASHOUT LIMITS
  getCashoutLimits(tierId: number): Observable<vo.GetCashoutLimitsResponse> {
    const url = `${Urls.disbursement}/${tierId}/getLimits`;
    return from(
      this.httpService.get(url) as Observable<vo.GetCashoutLimitsResponse>
    );
  }

  getAccountsTiers(): Observable<vo.AccountTiersResponse> {
    const url = `${Urls.tiers}/getAll`;
    return from(
      this.httpService.get(url) as Observable<vo.AccountTiersResponse>
    );
  }

  updateCashoutLimits(request: vo.UpdateCashoutLimitRequest, tierId: number): Observable<vo.BaseResponse> {
    const url = `${Urls.disbursement}/${tierId}/updateLimits`;
    return from(
      this.httpService.put(url, request) as Observable<vo.BaseResponse>
    );
  }

  // REBATES
  getRebateByTier(tierId: number): Observable<vo.GetBuyloadRebateResponse> {
    const url = `${Urls.buyloadRebate}/${tierId}/getAll`;
    return from(
      this.httpService.get(url) as Observable<vo.GetBuyloadRebateResponse>
    );
  }

  updateBuyloadRebate(request: vo.UpdateBuyloadRebateRequest, tierId: number): Observable<vo.BaseResponse> {
    const url = `${Urls.buyloadRebate}/${tierId}/update`;
    return from(
      this.httpService.put(url, request) as Observable<vo.BaseResponse>
    );
  }

  createBuyloadRebate(request: vo.CreateBuyloadRebateRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.buyloadRebate}/create`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  getDistributorsRebates(): Observable<vo.GetDistributorsRebatesResponse> {
    const url = `${Urls.rdpd}/getRebates`;
    return from(
      this.httpService.get(url) as Observable<vo.GetDistributorsRebatesResponse>
    );
  }
  
  modifyDistributorRebates(request: vo.EditDistributorRebateRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/setRebates`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }


  // REFERRALS
  getReferralsAccounts(page: number,  size: number,  request: vo.PortalUserLogsRequest): Observable<vo.GetReferralsResponse> {
    const url = `${Urls.referrals}/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetReferralsResponse>
    );
  }

  getReferredAccounts(page: number,  size: number,  request: vo.PortalUserLogsRequest): Observable<vo.GetReferralsResponse> {
    const url = `${Urls.referrals}/getAllByReferrerNumber?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetReferralsResponse>
    );
  }

  // THIRD PARTY LOGS
  getBuyloadLogs(page: number, size: number, request: vo.BuyloadLogsRequest): Observable<vo.BuyloadLogsResponse> {
    const url = `${Urls.buyloadlogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BuyloadLogsResponse>
    );
  }

  getPayBillsLogs(page: number, size: number, request: vo.PayBillsLogRequest): Observable<vo.PayBillsLogsResponse> {
    const url = `${Urls.paybillslogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.PayBillsLogsResponse>
    );
  }
  
  getCashInLogs(page: number, size: number, request: vo.CashInLogsRequest): Observable<vo.CashInLogsResponse> {
    const url = `${Urls.cashinlogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.CashInLogsResponse>
    );
  }

  getStoresLogs(page: number, size: number, request: vo.StoresLogsRequest): Observable<vo.StoresLogsResponse> {
    const url = `${Urls.storeslogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.StoresLogsResponse>
    );
  }

  getRedeemLogs(page: number, size: number, request: vo.RedeemLogsRequest): Observable<vo.RedeemLogsResponse> {
    const url = `${Urls.redeemlogs}?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.RedeemLogsResponse>
    );
  }

  // TIER FEATURES
  getAllTierFeatures(): Observable<vo.GetAllTierFeaturesResponse> {
    const url = `${Urls.features}/getAll`;
    return from(
      this.httpService.get(url) as Observable<vo.GetAllTierFeaturesResponse>
    );
  }

  updateTierFeatures(
    request: vo.UpdateTierFeatureRequest
  ): Observable<vo.BaseResponse> {
    return from(
      this.httpService.put(`${Urls.features}/update`, request) as Observable<vo.BaseResponse>
    );
  }

  createTierFeatures(
    request: vo.CreateTierFeatureRequest
  ): Observable<vo.BaseResponse> {
    return from(
      this.httpService.post(`${Urls.features}/create`, request) as Observable<vo.BaseResponse>
    );
  }


  // MAINTAINANCE
  getMaintainanceStatus(): Observable<{onMaintenanceMode: boolean}> {
    const url = `${Urls.maintenance}`;
    return from(
      this.httpService.get(url) as Observable<{onMaintenanceMode: boolean}>
    );
  }

  updateMaintainanceMode(mode: "enable" | "disable"): Observable<vo.BaseResponse> {
    const url = `${Urls.maintenance}/${mode}`;
    return from(
      this.httpService.get(url) as Observable<vo.BaseResponse>
    );
  }

  // VERSION CONTROL
  updateCheckAppVersionFlag(request: vo.UpdateCheckAppVersionFlagRequest): Observable<vo.CheckAppVersionFlagResponse> {

    // ONLY ACCEPT 0 OR 1 (1 = ON/ENABLED , 0 = OFF/DISABLED)
    if(request.checkAppVersionFlag !== 0 && request.checkAppVersionFlag !== 1){
      throw new Error("Invalid checkAppVersionFlag value. It must be either 0 or 1.");
    }else{
      const url = `${Urls.versionControls}/updateCheckAppVersionFlag`;
      return from(
        this.httpService.put(url, request) as Observable<vo.CheckAppVersionFlagResponse>
      );
    }
  }

  getCheckAppVersionFlag(): Observable<vo.CheckAppVersionFlagResponse> {
    const url = `${Urls.versionControls}/checkAppVersionFlag`;
    return from(
      this.httpService.get(url) as Observable<vo.CheckAppVersionFlagResponse>
    );
  }


  // RDPD DISTRIBUTORS
  distributorDoAction(request: vo.DistributorDoActionRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/doAction`;
    return from(this.httpService.put(url, request)) as Observable<vo.BaseResponse>;
  }

  exportDistributors(request: vo.ExportDistributorsRequest): Observable<Blob> {
    const url = `${Urls.rdpd}/exportAll`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  getDistributorsChain(page: number, size: number, request: vo.GetDistributorsChainRequest): Observable<vo.GetDistributionChainResponse> {
    const url = `${Urls.rdpd}/getDistributionChain?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetDistributionChainResponse>
    );
  }

  getAllDistributors(page: number, size: number, request: vo.GetAllDistributorsRequest): Observable<vo.GetAllDistributorsResponse> {
    const url = `${Urls.rdpd}/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetAllDistributorsResponse>
    );
  } 

  createRegionalDistributor(request: vo.CreateNewRDRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/createNewRd`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  createNewProvincialDistributor(request: vo.CreateNewPDRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/createNewPd`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }
  
  createNewRetailerDistributor(request: vo.CreateNewRetailerRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/createNewRt`;
    return from(
      this.httpService.post(url, request) as Observable<vo.BaseResponse>
    );
  }

  removeDistributor(request: vo.RemoveDistributorRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.rdpd}/removeDistributor?accountGuid=${request.accountGuid}`;
    return from(this.httpService.put(url, null)) as Observable<vo.BaseResponse>;
  }

  // MAYA REFRESH
  refreshMayaTransaction(request: vo.MayaRefreshRequest): Observable<vo.BaseResponse> {
    const url = `${Urls.transactionsCashin}/refreshMaya`;
    return from(this.httpService.post(url, request)) as Observable<vo.BaseResponse>;
  }


  // DISBURSEMENT INSTITUTIONS
  getDisbursementInstitutions(request: vo.GetDisbursementInstitutionsRequest, page: number, size: number): Observable<vo.GetDisbursementInstitutionsResponse> {
    const url = `${Urls.disbursement}/institutions/getAll?page=${page}&size=${size}`;
    return from(
      this.httpService.post(url, request) as Observable<vo.GetDisbursementInstitutionsResponse>
    );
  }

  exportDisbursementInstitutions(request: vo.ExportDisbursementInstitutionsRequest): Observable<Blob> {
    const url = `${Urls.disbursement}/institutions/export`;
    return from(this.httpService.postBlob(url, request)) as Observable<Blob>;
  }

  createDisbursementInstitution(formData: FormData): Promise<vo.DisbursementInstitutionResponse> {
    return this.httpService.postFormWithImage(
      `${Urls.disbursement}/institutions/createNew`,
      formData
    );
  }

  updateDisbursementInstitution(formData: FormData): Promise<vo.DisbursementInstitutionResponse> {
    return this.httpService.put2(
      `${Urls.disbursement}/institutions/update`,
      formData
    );
  }
}
